import { useAuth0 } from "@auth0/auth0-react";
import { DocumentMagnifyingGlassIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ArrowDownTrayIcon, ArrowPathIcon, DocumentTextIcon } from "@heroicons/react/24/solid";
import { ApiStatusToFileStatus } from "@utils/constants";
import { downloadLearnerFileById } from "@utils/helpers";
import { formatDate } from "@utils/utils";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { Badge, Column, Label, Paragraph, Small, Table, humanFileSize } from "@fronterahealth/frontera-ui-components";

import {
  ApiLearnerFileStatusChoices,
  LearnerFileType,
  useDeleteLearnerFileMutation,
  useGetLearnerFilesByLearnerQuery,
} from "@api/graphql/types-and-hooks";
import { DocumentReviewModal } from "@components/DocumentReviewModal/DocumentReviewModal";
import { FileStatusDisplay } from "@components/UploadDocuments/FileStatusDisplay";
import { convertReadableString } from "@components/forms/utils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

export const FileLibrary: React.FC = () => {
  const { clientId } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const [reviewFileId, setReviewFileId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const { data, isLoading, refetch } = useGetLearnerFilesByLearnerQuery(
    { learnerId: clientId ?? "" },
    { enabled: !!clientId },
  );

  const deleteLearnerFileMutation = useDeleteLearnerFileMutation({
    onSuccess: () => {
      notifySuccess("Successfully deleted file");
      return refetch();
    },
    onError: () => {
      notifyError("Failed to delete file. Something went wrong on our end");
    },
  });

  const nodes: LearnerFileType[] =
    data?.getLearnerFilesByLearner?.edges
      ?.map((edge) => edge?.node)
      .filter((node): node is LearnerFileType => node !== null && node !== undefined) ?? [];

  const handleDownload = async (fileId: string) => {
    setDownloadingId(fileId);
    try {
      const token = await getAccessTokenSilently();
      await downloadLearnerFileById({ fileId, token });
    } finally {
      setDownloadingId(null);
    }
  };

  const columns: Column<LearnerFileType>[] = [
    {
      title: "File Name",
      key: "originalFileName",
      dataIndex: "originalFileName",
      render: (_, record) => (
        <div className="flex items-center gap-3 min-w-0 overflow-hidden">
          <DocumentTextIcon className="h-5 w-5 text-icon-tertiary shrink-0" />
          <Label displayType="loud" size="small" colorType="primary" className="truncate">
            {record.originalFileName}
          </Label>
        </div>
      ),
    },
    {
      title: "Size",
      key: "fileSize",
      dataIndex: "fileSize",
      render: (_, record) => (
        <Label size="small" className="text-text-tertiary truncate">
          {humanFileSize(Number(record.fileSize) || 0)}
        </Label>
      ),
    },
    {
      title: "Type",
      key: "fileKind",
      dataIndex: "fileKind",
      render: (_, record) => convertReadableString(record.fileKind),
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (_, record) => (
        <FileStatusDisplay fileStatus={ApiStatusToFileStatus[record.status ?? ApiLearnerFileStatusChoices.Pending]} />
      ),
    },
    {
      title: "Effective Date",
      key: "effectiveDate",
      dataIndex: "effectiveDate",
      render: (_, record) => (record.effectiveDate ? formatDate(record.effectiveDate) : "—"),
    },
    {
      title: "Attached To",
      key: "reports",
      dataIndex: "id",
      render: (_, record) => {
        const reports = record.reports?.edges?.map((edge) => edge?.node).filter((r) => r) ?? [];
        if (reports.length === 0) return <span className="text-text-tertiary">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {reports.map(
              (r) => r && <Badge key={r.id} appearance="neutral" text={convertReadableString(r.reportType)} />,
            )}
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      dataIndex: "id",
      render: (_, record) => {
        const isDownloading = downloadingId === record.id;
        const isDeleting = deleteLearnerFileMutation.variables?.fileId === record.id;
        const attachedReports = record.reports?.edges?.map((edge) => edge?.node).filter((r) => r) ?? [];
        const isAttachedToReport = attachedReports.length > 0;
        return (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setReviewFileId(record.id)}
              className="text-text-primary hover:text-text-secondary animate-in-fade-left"
              data-testid="preview-file-button"
              aria-label="Preview"
            >
              <DocumentMagnifyingGlassIcon className="h-5 w-5 cursor-pointer" />
            </button>
            <button
              type="button"
              onClick={() => handleDownload(record.id)}
              className="text-text-primary hover:text-text-secondary animate-in-fade-left"
              data-testid="download-file-button"
              aria-label="Download"
              disabled={isDownloading}
            >
              {isDownloading ? (
                <ArrowPathIcon className="h-5 w-5 animate-spin-slow text-text-secondary" />
              ) : (
                <ArrowDownTrayIcon className="h-5 w-5 cursor-pointer" />
              )}
            </button>
            {!isAttachedToReport && (
              <button
                type="button"
                onClick={() => deleteLearnerFileMutation.mutate({ fileId: record.id })}
                className="text-text-primary hover:text-red-700 animate-in-fade-left"
                data-testid="delete-file-button"
                aria-label="Delete"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <ArrowPathIcon className="h-5 w-5 animate-spin-slow text-text-secondary" />
                ) : (
                  <TrashIcon className="h-5 w-5 cursor-pointer" />
                )}
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="flex w-full flex-col items-start gap-4 py-4">
        <div className="flow-root w-full">
          <Table
            data={nodes}
            columns={columns}
            loading={isLoading}
            emptyMessage={
              <div className="flex flex-col items-center">
                <Paragraph displayType="loud">No Files</Paragraph>
                <Small>Files uploaded for this client will show here</Small>
              </div>
            }
            showPagination
            showSearch
          />
        </div>
      </div>
      {reviewFileId && (
        <DocumentReviewModal
          fileId={reviewFileId}
          existingFiles={nodes}
          supportedAssessments={[]}
          onClose={() => setReviewFileId(null)}
          setFileId={(id) => setReviewFileId(id)}
        />
      )}
    </>
  );
};
